import { makeNonSerializableStateStore, makeStateStore } from '@playcastdotio/StateStore';
import { MakeClientPeerConfig, PlaycastRemotePeerClients, PlaycastClientPeerStatus } from './types';
import { log, debug, deepCloneTrackState, captureWebrtcError, recordDisconnectReason } from './util';
import { PlaycastWebrtcPreset, PlaycastWebrtcPresetGroup, PlaycastWebrtcStats } from './typesMedia';
import { defaultQualityPresetStore, serviceConfigs, USE_UDP_ONLY } from './defaultConfigs';
import {
  generators,
  PeerMessageType,
  PlaycastMessage,
  PlaycastMessagePeerChangeRequestedProfile,
  PlaycastMessagePeerFrameratePanic,
  PlaycastMessagePeerHandshake,
  PlaycastMessageTarget,
  validators,
  websocket,
} from '@playcastdotio/Messaging';
import Peer from '@bowbee/peer-lite';
import type {
  PlaycastChannels,
  PlaycastDefaultProfiles,
  PlaycastPeerClientMetadata,
  PlaycastPeerClientType,
  PlaycastPeerConnectionType,
  PlaycastPeerMediaMetadata,
  PlaycastTrackSet,
} from '@playcastdotio/CoreTypes';
import { parseStats } from './stats';
import { applyCodecPreferencesToPeer, applyPresetToTransceivers, doEncodersMatch, manageKeyframeInterval, transformSdp } from './presets';
import { addTrackWithMonitoring, monitorPeerConnection, replaceTrackWithMonitoring, verifyTransceiverSettings } from './debugging';
import { createMessageChunker } from './messageChunker';
import { startMetrics, stopMetrics } from './sentryStats';

const SHOULD_DEBUG = false;

export const createRemotePeer = (
  hostId: string,
  remoteId: string,
  type: PlaycastPeerClientType,
  config: MakeClientPeerConfig,
): PlaycastRemotePeerClients => {
  const status = makeStateStore<PlaycastClientPeerStatus>({ connectionState: 'signalling' });
  const connectionType = makeStateStore<PlaycastPeerConnectionType>('unknown');
  const statistics = makeStateStore<PlaycastWebrtcStats | null>(null);
  const profileState = makeStateStore<{ profile: PlaycastDefaultProfiles }>({ profile: 'default' });
  const qualityPresetStore = makeStateStore<PlaycastWebrtcPresetGroup>(defaultQualityPresetStore.currentState);
  qualityPresetStore.addListener((presetGroup) => {
    console.log('[Peer Quality Preset Group]', presetGroup);
  });
  const outgoingTracks = makeNonSerializableStateStore<PlaycastTrackSet>({});
  const rawPeer = new Peer({
    ...{ ...config.signalServerConfig, ...serviceConfigs.peerOptions },
    channelLabel: 'control',
    channelOptions: {
      ordered: true,
      maxRetransmits: 0,
    },
    batchCandidates: true,
    batchCandidatesTimeout: 1000,
    config: {
      ...serviceConfigs.peerOptions.config,
      ...(config.iceServers
        ? { iceServers: [config.iceServers] }
        : {
            iceServers: [
              {
                // fallback to google ICE servers
                urls: ['stun:stun.l.google.com:19302'],
              },
            ],
          }),
      iceTransportPolicy: config.forceTurn ? 'relay' : 'all',
    },
  });
  let metadata: PlaycastPeerClientMetadata = {
    peerId: remoteId,
    type,
    isAppleDevice: false,
    isFirefoxBrowser: false,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  };
  startMetrics(remoteId);
  const messageChunker = createMessageChunker(rawPeer);
  // messageChunker.setDebug(true);
  let messageCallback: ((message: PlaycastMessage<PlaycastMessageTarget>) => void) | undefined;
  let shouldHandlePanics = true;

  // Utility function to apply maintain-resolution failsafe
  const applyMaintainResolutionFailsafe = (
    trackId: string,
    preset: Partial<PlaycastWebrtcPreset>,
    metadata: PlaycastPeerClientMetadata,
  ) => {
    if (preset.videoSend) {
      const defaultMaintainFlag = preset.videoSend?.degradationPreference;
      const holdDelay = preset.videoSend?.maintainResolutionHoldDelay ?? 1500; // Default to 1500ms if not configured

      // Set maintain-resolution temporarily
      log('applyMaintainResolutionFailsafe - set', 'maintaining-resolution', holdDelay);
      preset.videoSend.degradationPreference = 'maintain-resolution';
      applyPresetToTransceivers(remoteId, rawPeer, trackId, preset, metadata, 'maintain-resolution');

      // Restore original degradation preference after configured delay
      setTimeout(() => {
        if (preset.videoSend) {
          log('applyMaintainResolutionFailsafe - restore', defaultMaintainFlag);
          preset.videoSend.degradationPreference = defaultMaintainFlag;
          applyPresetToTransceivers(remoteId, rawPeer, trackId, preset, metadata);
        }
      }, holdDelay);
    }
  };

  const statsCaptureInterval = setInterval(() => {
    const pc = rawPeer.get();
    parseStats(pc, 'remoteClient_' + remoteId).then((stats) => {
      statistics.updateState(stats);
    });
  }, 1000);

  const getMetaData = () => {
    return metadata;
  };

  const changeQualityProfile = (profile: PlaycastDefaultProfiles) => {
    debug('changeQualityProfile', profile);
    profileState.updateState({ profile });
  };
  const applyQualityPresetGroup = async (presetGroup: PlaycastWebrtcPresetGroup) => {
    debug('applyQualityPresetGroup', presetGroup);
    qualityPresetStore.updateState(presetGroup);
  };
  const applyQualityPreset = async (channel: PlaycastChannels, preset: Partial<PlaycastWebrtcPreset>) => {
    debug('applyQualityPreset', channel, preset);
    const presetGroup = qualityPresetStore.currentState;
    const newPresetGroup = {
      ...presetGroup,
      [channel]: {
        ...presetGroup[channel],
        ...preset,
      },
    };
    qualityPresetStore.updateState(newPresetGroup);
  };
  const onMessage = (callback: (message: PlaycastMessage<PlaycastMessageTarget>) => void) => {
    messageCallback = callback;
  };

  const CHUNK_SIZE = 16000; // 16KB chunks

  type ChunkedMessage = {
    id: string;
    index: number;
    total: number;
    data: string;
  };

  let pendingMessages: PlaycastMessage<PlaycastMessageTarget>[] = [];
  let pendingControlMessages: PlaycastMessage<PlaycastMessageTarget>[] = [];
  const sendMessage = (message: PlaycastMessage<PlaycastMessageTarget>, control?: boolean) => {
    const channelLabel = control ? 'control' : 'messages';
    messageChunker.sendMessage(message, channelLabel);
  };

  rawPeer.on('channelOpen', (e) => {
    if (e.channel.label === 'messages') {
      setTimeout(() => {
        pendingMessages.forEach((message) => {
          rawPeer.send(JSON.stringify(message), 'messages');
        });
        pendingMessages = [];
      }, 200);
    }
    if (e.channel.label === 'control') {
      setTimeout(() => {
        pendingControlMessages.forEach((message) => {
          rawPeer.send(JSON.stringify(message), 'control');
        });
        pendingControlMessages = [];
      }, 200);
    }
  });

  const retryInterval = setInterval(() => {
    if (pendingMessages.length > 0 || pendingControlMessages.length > 0) {
      [...pendingControlMessages].forEach((message, index) => {
        if (rawPeer.send(JSON.stringify(message), 'control')) {
          pendingControlMessages.splice(index, 1);
        }
      });

      [...pendingMessages].forEach((message, index) => {
        if (rawPeer.send(JSON.stringify(message), 'messages')) {
          pendingMessages.splice(index, 1);
        }
      });
    }
  }, 1000);

  const disconnect = async (refuse?: boolean, kickReason?: string) => {
    log('disconnect remote peer');
    try {
      recordDisconnectReason(kickReason ?? 'unknown', `remote:${remoteId}`);
    } catch (err) {
      console.warn('[Peer] Failed to record remote disconnect reason', err);
    }
    try {
      rawPeer.send(JSON.stringify(generators.peer.close('host', hostId, kickReason, remoteId)), 'control');
    } catch (e) {
      console.error('Error sending close message', e);
    }
    status.updateState({ connectionState: refuse ? 'refused' : 'closed', kickReason });

    manageKeyframeInterval.stopAllForPeer(remoteId);
    // Delay the disconnect to allow the close message to be sent, and keyframes to be stopped.
    await new Promise((resolve) => setTimeout(resolve, 200));
    try {
      rawPeer.destroy();
    } catch (e) {
      console.error('Error destroying peer', e);
    }
    clearInterval(retryInterval);
    clearInterval(statsCaptureInterval);
    stopMetrics(remoteId);
    messageChunker.cleanup();
    status.reset();
    connectionType.reset(true);
    statistics.reset(true);
    profileState.reset(true);
    qualityPresetStore.reset(true);
    outgoingTracks.reset(true);
  };

  const refuseConnection = () => {
    log('refuseConnection');
    disconnect(true, 'webrtc:refused_connection');
  };
  // const changeStatsInterval = (interval: number) => {
  //   log('changeStatsInterval', interval);
  // };
  const disableStats = () => {
    log('disableStats');
    clearInterval(statsCaptureInterval);
    statistics.reset();
  };
  const signal = (sdp: RTCSessionDescriptionInit) => {
    rawPeer.signal(sdp);
    // Attempt to flush any pending ICE candidates after setting remote/local description inside peer
    try {
      setTimeout(() => {
        const pc = rawPeer.get();
        if (!pc) return;
        const queue: RTCIceCandidate[] = (pc as any).__pendingRemoteIce || [];
        if (pc.remoteDescription && queue.length > 0) {
          (pc as any).__pendingRemoteIce = [];
          queue.forEach((c) => {
            try {
              rawPeer.addIceCandidate(c);
            } catch (e) {
              console.warn('[Peer] Failed to flush ICE candidate:', e);
            }
          });
        }
      }, 0);
    } catch {
      // ignore if transceivers not supported
    }
  };
  const addCandidate = (candidate: RTCIceCandidate) => {
    log('rawPeer.addCandidate', candidate);
    if (USE_UDP_ONLY) {
      try {
        const proto = (candidate as unknown as { protocol?: string })?.protocol;
        if (proto && proto.toLowerCase() !== 'udp') return;
        const candStr: string = (candidate as any).candidate ?? '';
        if (candStr && !candStr.includes(' udp ')) return;
      } catch {
        // ignore parse errors
      }
    }
    try {
      const pc = rawPeer.get();
      if (pc && !pc.remoteDescription) {
        (pc as any).__pendingRemoteIce = (pc as any).__pendingRemoteIce || [];
        ((pc as any).__pendingRemoteIce as RTCIceCandidate[]).push(candidate);
        return;
      }
    } catch {
      // ignore if transceivers not supported
    }
    rawPeer.addIceCandidate(candidate);
  };
  const addTracks = (tracks: PlaycastTrackSet) => {
    log('addTracks', tracks);
    let didUpdate = false;
    Object.keys(tracks).forEach((channelId) => {
      didUpdate = true;
      const track = tracks[channelId as PlaycastChannels];
      if (!track) return;
      if (!track.track) return;
      // const clonedTrack = track.track.clone();
      // outgoingTracks.currentState[channelId as PlaycastChannels] = { ...track, track: clonedTrack };
      outgoingTracks.currentState[channelId as PlaycastChannels] = track;
    });
    if (didUpdate) {
      outgoingTracks.updateState(outgoingTracks.currentState);
    }
  };
  const removeTracks = (tracks: PlaycastTrackSet) => {
    log('removeTracks', tracks);
    let didUpdate = false;
    Object.keys(tracks).forEach((channelId) => {
      didUpdate = true;
      delete outgoingTracks.currentState[channelId as PlaycastChannels];
    });
    if (didUpdate) {
      outgoingTracks.updateState(outgoingTracks.currentState);
    }
  };
  const setTracks = (tracks: PlaycastTrackSet) => {
    log('setTracks', tracks);
    // trace where the settracks is coming from
    // try {
    //   throw new Error('setTracks');
    // } catch (e: any) {
    //   console.log(e.stack);
    // }

    const currentTrackIds = Object.keys(outgoingTracks.currentState);
    Object.keys(tracks).forEach((channelId) => {
      const track = tracks[channelId as PlaycastChannels];
      if (!track) return;
      if (!track.track) return;
      // const clonedTrack = track.track.clone();
      // outgoingTracks.currentState[channelId as PlaycastChannels] = { ...track, track: clonedTrack };
      outgoingTracks.currentState[channelId as PlaycastChannels] = track;
    });
    // make sure we remove any tracks that are not in the new set
    currentTrackIds.forEach((channelId) => {
      if (!tracks[channelId as PlaycastChannels]) {
        delete outgoingTracks.currentState[channelId as PlaycastChannels];
      }
    });
    outgoingTracks.updateState(outgoingTracks.currentState);
  };
  const setShouldHandlePanics = (handlePanics: boolean) => {
    log('setShouldHandlePanics', handlePanics);
    shouldHandlePanics = handlePanics;
  };

  let qualityPresetCopy = { ...qualityPresetStore.currentState };
  let cooldown: NodeJS.Timeout | null = null;
  const handlePanic = (hardReset: boolean) => {
    if (hardReset) {
      // MODIFIED: Only remove/restore VIDEO tracks during panic to avoid disrupting audio
      const outgoingTracksCopy = deepCloneTrackState(outgoingTracks.currentState);

      // Separate audio and video tracks
      const videoTracks: PlaycastTrackSet = {};
      const audioTracks: PlaycastTrackSet = {};

      Object.entries(outgoingTracksCopy).forEach(([channel, trackData]) => {
        if (trackData) {
          if (trackData.track.kind === 'video') {
            videoTracks[channel as PlaycastChannels] = trackData;
          } else if (trackData.track.kind === 'audio') {
            audioTracks[channel as PlaycastChannels] = trackData;
          }
        }
      });

      console.log('Panic: Temporarily replacing video tracks only, preserving audio');

      // Only remove video tracks, keep audio tracks untouched
      removeTracks(videoTracks);

      // Create temporary black video only if we have video tracks to replace
      if (Object.keys(videoTracks).length > 0) {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (ctx) {
          canvas.width = 1920;
          canvas.height = 1080;
          ctx.fillStyle = 'black';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          const stream = canvas.captureStream(30);
          const videoTrack = stream.getVideoTracks()[0];

          if (videoTrack) {
            const mockShadowcam: PlaycastChannels = 'hostShadowCam00Video';
            const mockVideo: { [trackName: string]: { track: MediaStreamTrack; metadata: PlaycastPeerMediaMetadata } } = {
              [mockShadowcam]: {
                track: videoTrack,
                metadata: {
                  channel: mockShadowcam,
                  type: 'video',
                  displayName: 'Panic Recovery Video',
                },
              },
            };

            setTimeout(() => {
              addTracks(mockVideo);
              setTimeout(() => {
                removeTracks(mockVideo);
                setTimeout(() => {
                  // Only restore video tracks, audio tracks were never removed
                  addTracks(videoTracks);
                  // Apply maintain-resolution failsafe for restored video tracks
                  Object.entries(videoTracks).forEach(([, trackData]) => {
                    if (trackData && trackData.track.kind === 'video') {
                      const preset = {
                        ...qualityPresetStore.currentState[trackData.metadata.channel],
                        ...qualityPresetStore.currentState.default,
                      };
                      setTimeout(() => {
                        applyMaintainResolutionFailsafe(trackData.track.id, preset, metadata);
                      }, 300);
                    }
                  });
                }, 500);
              }, 500);
            }, 500);
          }
        }
      } else {
        console.log('Panic: No video tracks to reset, skipping panic sequence');
      }
    } else {
      // Change the framerate of the video tracks
      if (cooldown) return;
      debug('handlePanic', hardReset);
      const presetCopy = JSON.stringify(qualityPresetCopy);
      qualityPresetCopy = JSON.parse(presetCopy);
      const tempPresets: PlaycastWebrtcPresetGroup = JSON.parse(presetCopy);
      if (!tempPresets.default.videoSend) tempPresets.default.videoSend = defaultQualityPresetStore.currentState.default.videoSend;
      if (tempPresets.default.videoSend) {
        // pick a random number between 30 and 59.
        const randomFramerate = Math.floor(Math.random() * (59 - 30) + 30);
        tempPresets.default.videoSend.framerate = randomFramerate;
        // Vary the bitrate by +- 100kbps
        const randomBitrate = Math.floor(Math.random() * 200 - 100);
        if (tempPresets.default.videoSend.bitrate) {
          tempPresets.default.videoSend.bitrate += randomBitrate;
        }
      }
      // Get all trackIds
      const trackIds = Object.entries(outgoingTracks.currentState).map(([, trackData]) => {
        return trackData.track.id;
      });
      trackIds.forEach((trackId) => {
        if (!tempPresets.default.videoSend) return;
        const preset = tempPresets.default;
        applyPresetToTransceivers(remoteId, rawPeer, trackId, preset, metadata);
      });
      setTimeout(() => {
        debug('restoreFramerate');
        trackIds.forEach((trackId) => {
          console.log('Restoring framerate for track', trackId);
          const preset = qualityPresetCopy.default;
          applyPresetToTransceivers(remoteId, rawPeer, trackId, preset, metadata);
        });
      }, 1200);
      cooldown = setTimeout(() => {
        cooldown = null;
      }, 2400);
    }
  };

  const handleTrackUpdates = (newTracks: PlaycastTrackSet, cachedState: PlaycastTrackSet): PlaycastTrackSet => {
    const addedTracks: PlaycastTrackSet = {};
    const removedTracks: PlaycastTrackSet = {};

    // Remove any tracks that are ended
    Object.entries(cachedState).forEach(([, trackData]) => {
      if (trackData.track.readyState === 'ended') {
        manageKeyframeInterval.stop(remoteId, trackData.track.id);
        rawPeer.removeTrack(trackData.track, false);
        cachedState[trackData.metadata.channel as PlaycastChannels] = undefined;
      }
    });

    // Find added tracks
    Object.entries(newTracks).forEach(([channelId, trackData]) => {
      if (!cachedState[channelId as PlaycastChannels]) {
        addedTracks[channelId as PlaycastChannels] = trackData;
      }
    });
    debug('addedTracks', addedTracks);

    // Find removed tracks
    Object.entries(cachedState).forEach(([channelId, trackData]) => {
      if (!newTracks[channelId as PlaycastChannels]) {
        removedTracks[channelId as PlaycastChannels] = trackData;
      }
    });
    debug('removedTracks', removedTracks);

    // Handle track updates
    Object.entries(addedTracks).forEach(([, addedItem]) => {
      debug('addedItem', addedItem);
      if (!addedItem) return;

      let shouldReplaceTrack = false;
      let oldTrackToReplace: typeof addedItem | undefined;

      // Case 1: Shadow cam replacement
      if (addedItem.metadata.channel.startsWith('hostShadowCam')) {
        const shadowCamTrack = Object.entries(removedTracks).find(([, track]) => track?.metadata.channel.startsWith('hostShadowCam'));

        if (
          shadowCamTrack &&
          doEncodersMatch(
            addedItem.track,
            addedItem.metadata.channel as PlaycastChannels,
            shadowCamTrack[1].track,
            shadowCamTrack[1].metadata.channel as PlaycastChannels,
            qualityPresetStore,
          )
        ) {
          shouldReplaceTrack = true;
          oldTrackToReplace = shadowCamTrack[1];
        }
      }

      if (!shouldReplaceTrack) {
        const matchingKindTrack = Object.entries(removedTracks).find(([, track]) => track?.track.kind === addedItem.track.kind);

        if (
          matchingKindTrack &&
          doEncodersMatch(
            addedItem.track,
            addedItem.metadata.channel as PlaycastChannels,
            matchingKindTrack[1].track,
            matchingKindTrack[1].metadata.channel as PlaycastChannels,
            qualityPresetStore,
          )
        ) {
          shouldReplaceTrack = true;
          oldTrackToReplace = matchingKindTrack[1];
        }
      }

      const preset = { ...qualityPresetStore.currentState[addedItem.metadata.channel], ...qualityPresetStore.currentState.default };

      if (shouldReplaceTrack && oldTrackToReplace) {
        // debug('replaceTrack', oldTrackToReplace, addedItem);
        console.log(`[Track Replace] Replacing track with matching encoders:`, {
          oldTrackId: oldTrackToReplace.track.id,
          newTrackId: addedItem.track.id,
          preset: preset
            ? {
                codec: preset.videoReceive?.encoder,
                hasEncodings: !!preset.videoSend,
              }
            : 'No preset',
        });
        if (SHOULD_DEBUG) {
          replaceTrackWithMonitoring(rawPeer, oldTrackToReplace.track, addedItem.track, remoteId);
          // Apply maintain-resolution failsafe for track replacements in debug mode
          setTimeout(() => {
            if (addedItem.track.kind === 'video') {
              applyMaintainResolutionFailsafe(addedItem.track.id, preset, metadata);
            }
          }, 100);
        } else {
          rawPeer.replaceTrack(oldTrackToReplace.track, addedItem.track, false);

          setTimeout(() => {
            verifyTransceiverSettings(rawPeer, addedItem.track.id, preset);
            // Apply maintain-resolution failsafe for track replacements
            if (addedItem.track.kind === 'video') {
              applyMaintainResolutionFailsafe(addedItem.track.id, preset, metadata);
            }
          }, 100);
        }
        // Remove the replaced track from removedTracks to prevent double-handling
        Object.entries(removedTracks).forEach(([channelId, track]) => {
          if (track === oldTrackToReplace) {
            delete removedTracks[channelId as PlaycastChannels];
          }
        });
      } else {
        // debug('addTrack', addedItem);
        console.log(`[Track Add] Adding new track:`, {
          trackId: addedItem.track.id,
          kind: addedItem.track.kind,
          preset: preset
            ? {
                codec: preset.videoReceive?.encoder,
                hasEncodings: !!preset.videoSend,
              }
            : 'No preset',
        });
        // rawPeer.addStream(new MediaStream([addedItem.track]));
        if (SHOULD_DEBUG) {
          addTrackWithMonitoring(rawPeer, addedItem.track, remoteId);
          // Apply maintain-resolution failsafe for track additions in debug mode
          setTimeout(() => {
            if (addedItem.track.kind === 'video') {
              applyMaintainResolutionFailsafe(addedItem.track.id, preset, metadata);
            }
          }, 300);
        } else {
          console.log('Adding track with no debug', addedItem.track);
          rawPeer.addTrack(addedItem.track);
        }
        // Apply encodings after adding
      }
      if (preset) {
        applyPresetToTransceivers(remoteId, rawPeer, addedItem.track.id, preset, metadata);
        // Ensure codec preferences are set
        applyCodecPreferencesToPeer(rawPeer, qualityPresetStore.currentState, metadata, addedItem.metadata.channel);
      }

      setTimeout(() => {
        const valid = verifyTransceiverSettings(rawPeer, addedItem.track.id, preset);
        if (!valid) {
          // alert('Invalid transceiver settings');
          applyPresetToTransceivers(remoteId, rawPeer, addedItem.track.id, preset, metadata);
          applyCodecPreferencesToPeer(rawPeer, qualityPresetStore.currentState, metadata, addedItem.metadata.channel);
        }
        // Apply maintain-resolution failsafe for video track additions
        if (addedItem.track.kind === 'video') {
          applyMaintainResolutionFailsafe(addedItem.track.id, preset, metadata);
        }
      }, 300);
    });

    // Handle remaining removals
    Object.values(removedTracks).forEach((removedItem) => {
      if (removedItem) {
        manageKeyframeInterval.stop(remoteId, removedItem.track.id);
        rawPeer.removeTrack(removedItem.track, false);
      }
    });

    // Always make sure we re-set the profile settings

    // Return deep cloned new state
    return deepCloneTrackState(newTracks);
  };

  let cachedTrackState = deepCloneTrackState(outgoingTracks.currentState);
  outgoingTracks.addListener((tracks) => {
    log('outgoingTracks', tracks);
    const outgoingTrackMetadata = generateTrackMetadata(tracks);
    const trackMetadataMessage = generators.peer.updateTrackMetadata('host', hostId, remoteId, outgoingTrackMetadata);
    log('outgoingTrackMetadata', outgoingTrackMetadata);
    rawPeer.send(JSON.stringify(trackMetadataMessage), 'control');
    cachedTrackState = handleTrackUpdates(tracks, cachedTrackState);
  });

  qualityPresetStore.addListener((presetGroup) => {
    // Apply this preset group to the peer
    log('qualityPresetStore', presetGroup);
    Object.entries(outgoingTracks.currentState).forEach(([channelId, trackData]) => {
      const preset = presetGroup[channelId as PlaycastChannels];
      if (preset && trackData) {
        applyPresetToTransceivers(remoteId, rawPeer, trackData.track.id, preset, metadata);
      }
    });
  });

  // Check the transport status of the peer
  rawPeer.on('disconnected', () => {
    log('rawPeer.onDisconnected');
    disconnect(false, 'webrtc:transport_disconnected');
  });

  const peer: PlaycastRemotePeerClients = {
    remotePeerId: remoteId,
    status,
    connectionType,
    statistics,
    profileState,
    type,
    outgoingTracks,
    getMetaData,
    changeQualityProfile,
    applyQualityPresetGroup,
    applyQualityPreset,
    onMessage,
    sendMessage,
    disconnect,
    refuseConnection,
    disableStats,
    signal,
    addCandidate,
    addTracks,
    setTracks,
    removeTracks,
    setShouldHandlePanics,
  };

  rawPeer.on('signal', (sdp) => {
    // const transformedSdp = transformSdp(sdp, qualityPresetStore.currentState.default);
    const transformedSdp = sdp;
    log('[REMOTE] signal', transformedSdp);
    if (SHOULD_DEBUG) monitorPeerConnection(rawPeer, remoteId);
    if (!transformedSdp.sdp) {
      return;
    }
    config.realtimeApiManager.send(
      websocket.generators.peer(PeerMessageType.PEER_SDP_OFFER, {
        fromId: hostId,
        toId: remoteId,
        sdp: {
          sdp: transformedSdp.sdp,
          type: transformedSdp.type,
        },
      }),
    );
  });
  rawPeer.on('onicecandidates', (candidates) => {
    log('[REMOTE] onicecandidates', candidates);
    const filtered = USE_UDP_ONLY
      ? candidates.filter((c: RTCIceCandidate | string) => {
          try {
            const obj: any = typeof c === 'string' ? JSON.parse(c) : c;
            const proto = typeof obj.protocol === 'string' ? obj.protocol.toLowerCase() : null;
            if (proto) return proto === 'udp';
            const cand: string = obj.candidate ?? '';
            return cand.includes(' udp ');
          } catch {
            return true;
          }
        })
      : candidates;
    config.realtimeApiManager.send(
      websocket.generators.peer(PeerMessageType.PEER_ICE_CANDIDATE, {
        fromId: hostId,
        toId: remoteId,
        candidates: {
          json: filtered.map((candidate) => JSON.stringify(candidate)),
        },
      }),
    );
  });

  const handleIncomingMessage = (data: string, channelLabel: string) => {
    const parsedMessage = messageChunker.handleIncomingMessage(data, channelLabel);
    if (parsedMessage) {
      processMessage(parsedMessage, channelLabel);
    }
  };

  const processMessage = (message: string | Record<string, unknown>, channelLabel: string) => {
    const validMessage = validators.validatePlaycastMessage(message);
    if (!validMessage) {
      log('Invalid message received', message);
      return;
    }

    switch (channelLabel) {
      case 'messages': {
        if (validMessage.header.action === 'frameratePanic') {
          const hardReset = (validMessage as PlaycastMessage<PlaycastMessagePeerFrameratePanic>).body.message.hardReset;
          if (shouldHandlePanics) {
            handlePanic(hardReset);
          }
          return;
        }
        if (messageCallback) {
          messageCallback(validMessage);
        }
        break;
      }
      case 'control': {
        if (!validMessage) {
          log('Invalid message received', message);
          return;
        }
        if (validMessage.header.target !== 'peer') {
          log('Invalid message target', validMessage.header.target);
          return;
        }
        if (validMessage.header.action === 'close') {
          log('Received close message', validMessage);
          const reason = (validMessage as any)?.body?.message?.reason as string | undefined;
          disconnect(false, reason);
        }
        if (validMessage.header.action === 'handshake' && !validMessage.header.isReply) {
          const incomingMetadata = (validMessage as PlaycastMessage<PlaycastMessagePeerHandshake>).body.message;
          log('incomingMetadata', incomingMetadata);
          if (incomingMetadata.type === 'client' || incomingMetadata.type === 'remoteControlClient') {
            log('Valid peer type', incomingMetadata.type);
            metadata = incomingMetadata;
            status.updateState({ connectionState: 'validating' });
            if (rawPeer.getDataChannel('messages') === undefined) {
              rawPeer.addDataChannel('messages', {
                ordered: true,
                maxRetransmits: 0,
              });
            }
          } else {
            log('Invalid peer type', incomingMetadata.type);
            peer.refuseConnection();
          }
        }
        if (validMessage.header.action === 'changeRequestedProfile') {
          const profile = (validMessage as PlaycastMessage<PlaycastMessagePeerChangeRequestedProfile>).body.message.profile;
          profileState.updateState({ profile });
        }
        // log('rawPeer.onChannelData.control', message);
        break;
      }
    }
  };

  rawPeer.on('channelData', (data) => {
    if (data.source === 'outgoing') return;
    const payload = typeof data.data === 'string' ? data.data : '';
    handleIncomingMessage(payload, data.channel.label);
  });
  // rawPeer.init();
  // Ensure initial codec preferences are applied before negotiation starts
  applyCodecPreferencesToPeer(rawPeer, qualityPresetStore.currentState, metadata);
  rawPeer.start({ polite: true });
  const statusInterval = setInterval(() => {
    // get the status of the peer
    const peerStatus = rawPeer.status();
    log('peerStatus', peerStatus);
    if (peerStatus === 'connected') {
      clearInterval(statusInterval);
    }
    if (peerStatus === 'disconnected' || peerStatus === 'closed' || peerStatus === 'failed') {
      status.updateState({ connectionState: 'closed' });
      clearInterval(statusInterval);
    }
    if (peerStatus === 'completed') {
      status.updateState({ connectionState: 'closed' });
      clearInterval(statusInterval);
    }
  }, 200);
  // If the peer is still in signalling state after 20 seconds, close the connection
  setTimeout(() => {
    clearInterval(statusInterval);
    const currentState = status.currentState.connectionState;
    // Only log timeout if still in signalling AND not already closed
    if (currentState === 'signalling') {
      status.updateState({ connectionState: 'closed' });
      captureWebrtcError(rawPeer.get(), 'Peer connection timed out');
      manageKeyframeInterval.stopAllForPeer(remoteId);
      setTimeout(() => {
        // we delay here to allow keyframes to be stopped. Potential race conditions exist if we don't.
        rawPeer.destroy();
      }, 500);
    } else if (currentState === 'closed') {
      // Connection already closed by statusInterval, don't log timeout error
      log('Peer connection timeout check - already closed, skipping error log');
    }
  }, 20000);

  outgoingTracks.addListener((tracks) => {
    const filteredTracks = Object.values(tracks).map((track) => {
      return {
        type: track.metadata.type,
        channel: track.metadata.channel,
        displayName: track.metadata.displayName || 'none',
        trackId: track.track.id,
      };
    });
    if (filteredTracks.length === 0) {
      filteredTracks.push({
        type: 'No type',
        channel: 'No channel',
        displayName: 'No display name',
        trackId: 'No tracks',
      } as never);
    }
    log('Outgoing tracks to remote peer', remoteId);
    console.table(filteredTracks);
  });

  status.addListener(async (status) => {
    if (status.connectionState === 'connected') {
      const stats = await rawPeer.get().getStats();
      let localCandidateType: string | null = null;
      let remoteCandidateType: string | null = null;
      let localCandidateId = '';
      let remoteCandidateId = '';

      stats.forEach((report) => {
        if (report.type === 'candidate-pair' && report.state === 'succeeded') {
          localCandidateId = report.localCandidateId;
          remoteCandidateId = report.remoteCandidateId;
        }
      });

      stats.forEach((report) => {
        // console.log(report);
        if (report.type === 'local-candidate' && report.id === localCandidateId) {
          localCandidateType = report.candidateType;
        }
        if (report.type === 'remote-candidate' && report.id === remoteCandidateId) {
          remoteCandidateType = report.candidateType;
        }
      });

      // Helper function to get the "weakest" connection type between two candidates
      const getWeakestConnectionType = (type1: string | null, type2: string | null): PlaycastPeerConnectionType => {
        const strengthOrder: { [key: string]: number } = {
          host: 1,
          srflx: 2,
          prflx: 3,
          relay: 4,
        };

        // If either type is null/invalid, return the other type or 'unknown'
        if (!type1 && !type2) return 'unknown';
        if (!type1) return type2 as PlaycastPeerConnectionType;
        if (!type2) return type1 as PlaycastPeerConnectionType;

        // Return the type with the higher number (weaker connection)
        return (strengthOrder[type1] >= strengthOrder[type2] ? type1 : type2) as PlaycastPeerConnectionType;
      };

      // Determine the weakest link between local and remote candidates
      const weakestType = getWeakestConnectionType(localCandidateType, remoteCandidateType);
      connectionType.updateState(weakestType);
    }
  });

  return peer;
};

const generateTrackMetadata = (tracks: PlaycastTrackSet) => {
  const metadata: Record<string, PlaycastPeerMediaMetadata> = {};
  Object.entries(tracks).forEach(([, trackData]) => {
    metadata[trackData.track.id] = {
      ...trackData.metadata,
    };
  });
  return metadata;
};
